/**
 * ActionRegistry - central aggregator for data-action handlers
 *
 * Modules call registerMap(namespace, mapping) during their setup.
 * App calls installAll(actionRouter) once, which installs all mappings
 * in a deterministic order and warns on duplicate action keys across
 * namespaces (first writer wins).
 */

/** @type {Map<string, Map<string, any>>} */
const _registry = new Map(); // ns -> Map(action -> fn|meta)
/** @type {Set<string>} */
const _installedNamespaces = new Set();

/**
 * @param {string} namespace
 * @param {Record<string, any>} mapping
 */
export function registerMap(namespace, mapping) {
  const ns = namespace || 'global';
  const current = _registry.get(ns) || new Map();
  Object.entries(mapping || {}).forEach(([k, v]) => {
    if (!k || typeof v !== 'function' && typeof v !== 'object') return;
    current.set(k, v);
  });
  _registry.set(ns, current);

  // If a global router ref is available (central mode already installed),
  // register immediately so late-mounted modules (e.g., Settings) work.
  try {
    const router = (typeof window !== 'undefined') ? window.__ACTION_ROUTER_REF__ : null;
    if (router && typeof router.register === 'function') {
      const obj = {};
      // Only register keys not already installed
      for (const [k, v] of current.entries()) {
        const has = !!(router.handlers && typeof router.handlers.has === 'function' && router.handlers.has(k));
        if (!has) obj[k] = v;
      }
      if (Object.keys(obj).length) router.register(ns, obj);
      _installedNamespaces.add(ns);
    }
  } catch (_) {}
}

export function getAll() {
  return new Map(_registry);
}

export function clear() {
  _registry.clear();
}

/**
 * @param {{register: Function}} actionRouter
 */
export function installAll(actionRouter) {
  if (!actionRouter) return;
  // Cross-namespace duplicate detection (first wins)
  const seen = new Set();
  for (const [ns, map] of _registry.entries()) {
    // Skip namespaces already installed live
    if (_installedNamespaces.has(ns)) continue;
    const filtered = {};
    const dups = [];
    for (const [key, val] of map.entries()) {
      if (seen.has(key)) { dups.push(key); continue; }
      filtered[key] = val;
      seen.add(key);
    }
    if (Object.keys(filtered).length) actionRouter.register(ns, filtered);
    if (dups.length) {
      try { console.error('[ActionRegistry] Duplicate keys ignored for namespace', ns, dups); } catch (_) {}
    }
  }
}

export default { registerMap, installAll, getAll, clear };
